// Which deployments a deal's cost screens count.
//
// Nonprod costs real money and nobody can bill it. That is a reason to show it
// plainly and never to add it to a billable total, so a nonprod chip exists and
// starts off (docs/crm-cost-explorer.md section 3).
//
// The choice is per deal, because a person who works one deal with nonprod on
// keeps it there and every other deal is unaffected. It is stored in this
// browser and it is also in the URL, and the URL wins: a link somebody sends
// has to mean what it says on the screen they were looking at.

export const DEPLOYMENT_SELECTION_PREFIX = "crm.usage.deployments.";

/** The environment is stated in the slug, which is the only place it is stated. */
export function isNonprod(slug: string): boolean {
  return slug.startsWith("nonprod-");
}

/** Production on, nonprod off. The selection a screen opens with when nothing says otherwise. */
export function defaultDeployments(available: readonly string[]): string[] {
  const production = available.filter((slug) => !isNonprod(slug));
  // A deal whose every deployment is nonprod would otherwise open on an empty
  // scope and report nothing at all, which reads as "this deal costs nothing".
  return production.length === 0 ? [...available] : production;
}

function storageKey(dealId: string): string {
  return `${DEPLOYMENT_SELECTION_PREFIX}${dealId}`;
}

/**
 * What this browser last chose for this deal, or null.
 *
 * Every access is guarded: a browser set to block site data throws on the
 * accessor itself, and a screen that cannot remember a chip selection must
 * still draw the month.
 */
export function readStoredDeployments(dealId: string): string[] | null {
  try {
    const raw = window.localStorage.getItem(storageKey(dealId));
    if (raw === null || raw === "") return null;
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    const slugs = parsed.filter((value): value is string => typeof value === "string");
    return slugs.length === 0 ? null : slugs;
  } catch {
    return null;
  }
}

export function writeStoredDeployments(dealId: string, slugs: readonly string[]): void {
  try {
    window.localStorage.setItem(storageKey(dealId), JSON.stringify([...slugs]));
  } catch {
    // Nothing to do and nothing to say: the selection still holds for this view.
  }
}

export interface DeploymentResolution {
  readonly dealId: string;
  /** Every deployment this deal has a binding on, in the order they are shown. */
  readonly available: readonly string[];
  /** What the URL asked for, when it asked. */
  readonly fromUrl: readonly string[] | undefined;
}

/**
 * The selection in force: the URL, else what this browser remembers, else
 * production only.
 *
 * A slug the deal has no binding for is dropped rather than kept, so an old link
 * to a deployment this deal has left cannot filter the screen down to nothing.
 */
export function resolveDeployments({ dealId, available, fromUrl }: DeploymentResolution): string[] {
  const known = (slugs: readonly string[]): string[] =>
    slugs.filter((slug) => available.includes(slug));

  if (fromUrl !== undefined) {
    const fromLink = known(fromUrl);
    if (fromLink.length > 0) return fromLink;
  }
  const stored = readStoredDeployments(dealId);
  if (stored !== null) {
    const remembered = known(stored);
    if (remembered.length > 0) return remembered;
  }
  return defaultDeployments(available);
}
